import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface EssayEvent {
  type: 'essay:updated' | 'essay:created' | 'essay:deleted';
  slug: string;
  timestamp: string;
}

interface UseEssayEventsOptions {
  /** Called when an essay is updated externally (filesystem, other tab) */
  onExternalUpdate?: (slug: string) => void;
}

/**
 * SSE hook for real-time essay change notifications.
 * Invalidates React Query caches when essays change from external sources
 * (filesystem edits, other tabs).
 */
export function useEssayEvents(options?: UseEssayEventsOptions) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const onExternalUpdateRef = useRef(options?.onExternalUpdate);

  // Keep callback ref up to date without re-creating effect
  useEffect(() => {
    onExternalUpdateRef.current = options?.onExternalUpdate;
  }, [options?.onExternalUpdate]);

  useEffect(() => {
    const es = new EventSource('/api/essays/events', { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data: EssayEvent = JSON.parse(event.data);

        switch (data.type) {
          case 'essay:created':
            queryClient.invalidateQueries({ queryKey: ['essays'] });
            break;

          case 'essay:updated':
            queryClient.invalidateQueries({ queryKey: ['essay', data.slug] });
            queryClient.invalidateQueries({ queryKey: ['essays'] });
            onExternalUpdateRef.current?.(data.slug);
            break;

          case 'essay:deleted':
            queryClient.invalidateQueries({ queryKey: ['essays'] });
            queryClient.invalidateQueries({ queryKey: ['essay', data.slug] });
            break;
        }
      } catch {
        // Ignore malformed events
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects — no action needed
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [queryClient]);
}
